import { Controller } from "@hotwired/stimulus"
import ComposerFilePicker from "../utils/composer_file_picker"
import VoiceRecorder from "../utils/voice_recorder"

// Unified chat + voice composer.
// - With text: the button submits the message like a normal chat.
// - Empty: the mic is push-to-talk, like every composer mic in the app. Tap
//   to record, tap again to stop; the audio is transcribed into the field and
//   submitted as an ordinary message, so the reply comes back as text only.
// Spoken replies are the call's job, not the mic's: a hands-free call starts
// from the waveform beside the mic, from the phone in the
// conversation's header (or the + menu's "Call" where there is no header) via
// the app-wide `assistant-voice:open` event — see startCall.
export default class extends Controller {
    static targets = ["field", "button", "icon", "voice", "searchButton", "webSearch", "deepResearch", "background",
                      "searchItem", "modeTag", "modeTagIcon", "modeTagLabel",
                      "fileInput", "attachPanel", "attachSearchInput", "attachResults", "attachmentsRow"]
    static values = { filesIndexUrl: String, filesSearchUrl: String, conversationId: String, draftOwner: String }

    connect() {
        this._state = "idle" // idle | recording | transcribing
        this._recorder = new VoiceRecorder({
            onState: (state) => this._setState(state),
            onText: (text) => this._submitVoice(text),
            onError: (message) => this._flash(message)
        })
        // Staged attachments, sent on the next submit: { localId, kind: "upload", file }
        // for a fresh OS upload, or { localId, kind: "library", id, name } for a
        // file picked from the existing Files library. Nothing uploads until
        // the message itself is sent (see onSubmit).
        this._attachments = []
        this._attachSeq = 0
        this._filePicker = new ComposerFilePicker(this)
        this._submitting = false
        // Clear the retired menu modes; commands and automatic tools choose
        // research per message instead of retaining a hidden preference.
        this._searchMode = "off"
        sessionStorage.removeItem("assistantSearchMode")
        this._syncSearch()
        // Before _sync(), which reads fieldTarget.value to decide mic vs send.
        this._beforeChoiceChange = () => {
            if (this._draftSaveTimer) clearTimeout(this._draftSaveTimer)
            this._saveDraftNow()
            this.fieldTarget.value = ""
        }
        this._afterChoiceChange = () => { this._restoreDraft(); this._sync() }
        this.element.addEventListener("composer-choices:before-change", this._beforeChoiceChange)
        this.element.addEventListener("composer-choices:changed", this._afterChoiceChange)
        this._restoreDraft()
        this._sync()
    }

    // The composer waveform uses the same event the header phones raise
    // (jane_sidebar#call, voice-call#start). The call bar is app-wide
    // (shared/_jane_call_dock); tell it which thread the call belongs to.
    startCall(e) {
        e?.preventDefault()
        if (this.element.dataset.sharedProject === "true") { this._flash("Live calls are available in private chats."); return }
        const conversationId = this.hasConversationIdValue && this.conversationIdValue ? this.conversationIdValue : null
        window.dispatchEvent(new CustomEvent("assistant-voice:open", { detail: { conversationId } }))
    }

    // Cycles the globe button: off -> web search -> deep research -> off.
    toggleSearch(e) {
        e.preventDefault()
        const next = { off: "web", web: "deep", deep: "off" }
        this._searchMode = next[this._searchMode] || "web"
        sessionStorage.setItem("assistantSearchMode", this._searchMode)
        this._syncSearch()
        this._sync()
    }

    // The + menu's search items: tap turns a mode on, tapping the active one
    // turns it off (no three-way cycle — each mode is its own row).
    setSearchMode(e) {
        const mode = e.currentTarget?.dataset?.searchMode
        if (!mode) return
        this._searchMode = this._searchMode === mode ? "off" : mode
        sessionStorage.setItem("assistantSearchMode", this._searchMode)
        this._syncSearch()
        this._sync()
    }

    // The echo tag beside the chip doubles as the off switch.
    clearSearchMode() {
        this._searchMode = "off"
        sessionStorage.setItem("assistantSearchMode", "off")
        this._syncSearch()
        this._sync()
    }

    _syncSearch() {
        if (this.hasSearchButtonTarget) {
            const btn = this.searchButtonTarget
            btn.classList.remove("is-web", "is-deep")
            let label = "Search the web"
            if (this._searchMode === "web") {
                btn.classList.add("is-web")
                label = "Web search on - tap for deep research"
            } else if (this._searchMode === "deep") {
                btn.classList.add("is-deep")
                label = "Deep research on - tap to turn off"
            }
            btn.title = label
            btn.setAttribute("aria-label", label)
        }
        this.searchItemTargets.forEach((item) => {
            item.classList.toggle("is-active", item.dataset.searchMode === this._searchMode)
        })
        if (this.hasModeTagTarget) {
            const tag = {
                web: ["bi bi-globe2", "Web search"],
                deep: ["bi bi-binoculars", "Deep research"],
                background: ["bi bi-hourglass-split", "Background task"]
            }[this._searchMode] || ["bi bi-globe2", "Web search"]
            this.modeTagTarget.hidden = this._searchMode === "off"
            if (this.hasModeTagIconTarget) this.modeTagIconTarget.className = tag[0]
            if (this.hasModeTagLabelTarget) this.modeTagLabelTarget.textContent = tag[1]
        }
        if (this.hasWebSearchTarget) this.webSearchTarget.value = this._searchMode === "web" ? "1" : ""
        if (this.hasDeepResearchTarget) this.deepResearchTarget.value = this._searchMode === "deep" ? "1" : ""
        if (this.hasBackgroundTarget) this.backgroundTarget.value = this._searchMode === "background" ? "1" : ""
    }

    _idlePlaceholder() {
        // A universal-compose destination owns the placeholder while active
        // (set via data-placeholder-override on the field) — search modes
        // only narrate when the message is going to Jane.
        const override = this.hasFieldTarget && this.fieldTarget.dataset.placeholderOverride
        if (override) return override
        if (this._searchMode === "background") return "Describe a task for Jane to work on\u2026"
        if (this._searchMode === "deep") return "Deep research the web\u2026"
        if (this._searchMode === "web") return "Search the web\u2026"
        return "Ask me anything\u2026"
    }

    disconnect() {
        this.element.removeEventListener("composer-choices:before-change", this._beforeChoiceChange)
        this.element.removeEventListener("composer-choices:changed", this._afterChoiceChange)
        this._filePicker.disconnect()
        this._recorder.cancel()
        if (this._draftSaveTimer) clearTimeout(this._draftSaveTimer)
    }

    onInput() {
        if (this._state === "idle") this._sync()
        this._persistDraft()
    }

    // --- draft persistence: a refresh or navigating away used to lose
    // whatever the reader was mid-typing — Jane has no autosave-to-server for
    // an unsent message, so the only copy lived in the DOM. Mirrors
    // dm_compose_controller's own local draft (_restoreDraft/_persistDraft/
    // storageKeyValue, ~lines 10, 78-109 there), keyed by conversation id
    // since this exact composer markup is what renders on every thread. A
    // "new chat" surface (home's bar, the sidebar panel before a thread
    // exists, /compose) has no id yet and shares one "new" bucket — good
    // enough since none of those are a thread worth isolating from each
    // other, and it matches how little state a not-yet-started chat has.
    _draftKey() {
        const id = this.element.querySelector("[name='conversation_id']:not(:disabled)")?.value || "new"
        const project = this.element.querySelector("[name='project_id']")?.value || "none"
        return `assistant-draft-${this.draftOwnerValue}-${id}-${project}`
    }

    _restoreDraft() {
        try {
            const saved = localStorage.getItem(this._draftKey())
            if (saved && !this.fieldTarget.value) this.fieldTarget.value = saved
        } catch {
            // private browsing
        }
    }

    _persistDraft() {
        if (this._draftSaveTimer) clearTimeout(this._draftSaveTimer)
        this._draftSaveTimer = setTimeout(() => this._saveDraftNow(), 300)
    }

    saveDraft() {
        clearTimeout(this._draftSaveTimer)
        this._saveDraftNow()
    }

    get hasStagedAttachments() { return this._attachments.length > 0 }
    get isBusy() { return this._state !== "idle" || this._submitting }

    _saveDraftNow() {
        try {
            const text = this.fieldTarget.value
            if (text.trim()) localStorage.setItem(this._draftKey(), text)
            else localStorage.removeItem(this._draftKey())
        } catch {
            // Storage may be disabled in private browsing.
        }
    }

    _clearDraft() {
        try {
            localStorage.removeItem(this._draftKey())
        } catch {
            // ignore
        }
    }

    onKeydown(e) {
        if (e.key === "Escape" && this._state === "recording") {
            e.preventDefault()
            this._recorder.cancel()
        }
    }

    // --- attachments: the + menu's "Upload a file" / "Choose from Files" ---

    pickFile() {
        if (this.hasFileInputTarget) this.fileInputTarget.click()
    }

    // Stages each selected file as a chip — nothing is uploaded yet. The
    // input is cleared right after so choosing the exact same file again
    // (e.g. re-adding one just removed) still fires a change event.
    filesChosen(e) {
        this._stageUploads(Array.from(e.target.files || []))
        e.target.value = ""
    }

    // A screenshot or photo pasted into the field is staged like an upload
    // from the + menu. Text pastes are left alone, and so is a composer
    // without the + menu, which has nowhere to show or send a file.
    paste(e) {
        if (!this.hasAttachmentsRowTarget) return
        const files = Array.from(e.clipboardData?.files || [])
        if (files.length === 0 || e.clipboardData.getData("text/plain")) return
        e.preventDefault()
        this._stageUploads(files)
    }

    _stageUploads(files) {
        if (!files.length) return
        files.forEach((file) => {
            this._attachments.push({ localId: `up-${++this._attachSeq}`, kind: "upload", file, name: file.name })
        })
        this._renderAttachments()
        this._sync()
    }

    openFilePicker() {
        this._filePicker.openFilePicker()
    }

    closeAttachPanel() {
        this._filePicker.closeAttachPanel()
    }

    searchFiles() {
        this._filePicker.searchFiles()
    }

    attachSearchKeydown(event) {
        this._filePicker.attachSearchKeydown(event)
    }

    // A library pick carries only an id — the server re-resolves it against
    // current_user.user_files (never a bare id lookup), so a stale or
    // someone-else's id just silently attaches nothing.
    pickLibraryFile(e) {
        const id = e.currentTarget.dataset.id
        const name = e.currentTarget.dataset.name
        if (!id) return
        if (!this._attachments.some((a) => a.kind === "library" && a.id === id)) {
            this._attachments.push({ localId: `lib-${id}`, kind: "library", id, name })
        }
        this.closeAttachPanel()
        this._renderAttachments()
        this._sync()
    }

    removeAttachment(e) {
        const localId = e.currentTarget.dataset.localId
        this._attachments = this._attachments.filter((a) => a.localId !== localId)
        this._renderAttachments()
        this._sync()
    }

    _renderAttachments() {
        if (!this.hasAttachmentsRowTarget) return
        const row = this.attachmentsRowTarget
        row.innerHTML = ""
        row.hidden = this._attachments.length === 0
        this._attachments.forEach((a) => {
            const chip = document.createElement("span")
            chip.className = "composer-attachment-chip"
            const icon = document.createElement("i")
            icon.className = a.kind === "upload" ? "bi bi-paperclip" : "bi bi-folder2"
            icon.setAttribute("aria-hidden", "true")
            const label = document.createElement("span")
            label.className = "composer-attachment-chip-name"
            label.textContent = a.name
            const remove = document.createElement("button")
            remove.type = "button"
            remove.className = "composer-attachment-chip-remove"
            remove.setAttribute("aria-label", `Remove ${a.name}`)
            remove.dataset.localId = a.localId
            remove.dataset.action = "click->assistant-compose#removeAttachment"
            remove.innerHTML = '<i class="bi bi-x" aria-hidden="true"></i>'
            chip.appendChild(icon)
            chip.appendChild(label)
            chip.appendChild(remove)
            row.appendChild(chip)
        })
    }

    _clearAttachments() {
        this._attachments = []
        this._renderAttachments()
    }

    // --- sending to Jane (FormData + fetch, same shape as _transcribe below)
    // so staged File objects can ride along as multipart parts. universal-
    // compose#onSubmit owns every OTHER destination and returns early
    // (without preventDefault()) once it sees the field is still routed to
    // Jane (name="message") — this handler runs right after it in the same
    // submit event and takes over from there.
    onSubmit(e) {
        if (this.fieldTarget.name !== "message") return
        e.preventDefault()
        if (this._state !== "idle" || this._submitting) return
        if (!this._hasText()) return

        const form = this.element
        const body = new FormData(form)
        this._attachments.forEach((a) => {
            if (a.kind === "upload") body.append("attachments[]", a.file, a.file.name)
            else body.append("user_file_ids[]", a.id)
        })

        // A pane that hosts the thread in place (the Jane sidebar, the
        // extension's framed pane, a page already showing this thread) shows
        // the send right away: the text leaves
        // the field and lands in the thread as your own bubble with Jane's
        // typing dots under it. The chat's next render replaces both with the
        // real turns; a failure takes them back out and gives the text back.
        const hosted = !!this._pane()
        const sentText = this.fieldTarget.value
        if (hosted) {
            if (this._optimisticTranscribing) this._setOptimisticText(sentText)
            else this._showOptimistic(sentText, this._attachments.map((a) => a.name || a.file?.name).filter(Boolean))
            this.fieldTarget.value = ""
        }

        this._submitting = true
        this._setSending(true)
        fetch(form.action, {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
            },
            credentials: "same-origin",
            body
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.ok) {
                    if (data.feedback_url) {
                        this.fieldTarget.value = ""
                        this._clearDraft()
                        // The feedback page is a whole page: beside the sidebar
                        // it opens in its own tab rather than replacing the page.
                        // A thread page has nothing to keep, so it goes there.
                        if (hosted) this._removeOptimistic()
                        if (this.element.closest(".detail-sidebar--jane, [data-jane-pane]")) {
                            window.open(data.feedback_url, "_blank", "noopener")
                            return
                        }
                        if (window.Turbo) window.Turbo.visit(data.feedback_url)
                        else window.location.href = data.feedback_url
                        return
                    }
                    this.element.dispatchEvent(new CustomEvent("assistant-compose:sent", { bubbles: true, detail: { conversationId: data.conversation_id } }))
                    // A background task is one request, not a mode to stay in:
                    // the next message is steering, or an ordinary chat.
                    if (this._searchMode === "background") this.clearSearchMode()
                    this.fieldTarget.value = ""
                    this._clearAttachments()
                    this._clearDraft()
                    this._sync()
                    // Inside the Jane sidebar (persona, family tree, tasks, …) the
                    // thread is hosted in place: tell the sidebar which thread to
                    // follow instead of navigating the page away to /messages/:id.
                    // The extension's framed pane (assistants#panel, [data-jane-pane])
                    // hosts it in place the same way.
                    if (this.element.closest(".detail-sidebar--jane, [data-jane-pane]")) {
                        this.element.dispatchEvent(new CustomEvent("jane-sidebar:sent", {
                            bubbles: true,
                            detail: { conversationId: data.conversation_id }
                        }))
                        return
                    }
                    // The Messages drawer (messages-sidebar) hosts the thread in
                    // place too: it reloads on assistant-compose:sent above.
                    if (this.element.closest(".messages-drawer")) return
                    // A page already showing this thread (/messages/:id, or the
                    // thread beside the Messages list) keeps it and polls for the
                    // reply. Visiting the thread's URL instead replaced the whole
                    // page, and Turbo painted its cached copy first - whatever the
                    // thread looked like on the last visit, the starter chips of
                    // an empty thread included.
                    const poller = this._threadPoller()
                    if (poller) {
                        this.application.getControllerForElementAndIdentifier(poller, "assistant-poll")?.restart()
                        return
                    }
                    const url = data.conversation_id ? `/messages/${encodeURIComponent(data.conversation_id)}` : null
                    if (url) {
                        if (window.Turbo) window.Turbo.visit(url)
                        else window.location.href = url
                    }
                } else {
                    this._sendFailed(hosted, sentText, data.error)
                }
            })
            .catch(() => this._sendFailed(hosted, sentText))
            .finally(() => {
                this._submitting = false
                this._setSending(false)
                if (hosted) this._sync()
            })
    }

    _sendFailed(hosted, text, error) {
        if (hosted) {
            this._removeOptimistic()
            if (!this.fieldTarget.value) this.fieldTarget.value = text
        }
        this._flash(error || "Couldn't send — try again.")
    }

    // --- Optimistic send, for the panes that host the thread in place

    _pane() {
        return this.element.closest(".detail-sidebar--jane, [data-jane-pane]") || this._threadPoller()
    }

    // The poller of the thread this composer writes to, when the page already
    // shows that thread around it: the full thread page, or the thread panel
    // beside the Messages list. Not for a new thread (no conversation_id yet).
    _threadPoller() {
        const id = this.element.querySelector("input[name='conversation_id']")?.value
        if (!id) return null
        const poller = this.element.closest("[data-controller~='assistant-poll']")
        return poller && poller.dataset.assistantPollConversationIdValue === String(id) ? poller : null
    }

    // Your message and a pending reply, appended to the pane's chat before the
    // request is answered. Both are marked data-optimistic so they can be told
    // from real turns; the pending one carries data-assistant-pending so the
    // poller (restarted once the send lands) treats the reply as in flight and
    // keeps the pane pinned to your message through the swap.
    _showOptimistic(text, fileNames = [], { transcribing = false } = {}) {
        const pane = this._pane()
        const scroller = pane?.querySelector(".msg-detail-scroll") || pane?.querySelector(".assistant-content")
        if (!scroller) return
        this._removeOptimistic()

        let chat = scroller.querySelector(".assistant-chat")
        if (!chat) {
            // A pane with no thread yet shows starter chips instead of a chat.
            chat = document.createElement("div")
            chat.className = "assistant-chat"
            chat.dataset.optimistic = "true"
            const empty = scroller.querySelector(".assistant-chat-empty")
            if (empty) {
                empty.style.display = "none"
                this._optimisticEmpty = empty
                empty.after(chat)
            } else {
                scroller.appendChild(chat)
            }
        }

        const mine = document.createElement("div")
        mine.className = "assistant-bubble assistant-bubble-user"
        mine.dataset.optimistic = "true"
        mine.textContent = transcribing ? "Transcribing\u2026" : text
        const nodes = [mine]
        if (fileNames.length) {
            const files = document.createElement("div")
            files.className = "assistant-turn-attachments"
            files.dataset.optimistic = "true"
            fileNames.forEach((name) => {
                const chip = document.createElement("span")
                chip.className = "composer-attachment-chip"
                chip.textContent = name
                files.appendChild(chip)
            })
            nodes.push(files)
        }
        const pending = document.createElement("div")
        pending.className = "assistant-bubble assistant-bubble-assistant"
        pending.dataset.optimistic = "true"
        pending.dataset.assistantPending = "true"
        pending.innerHTML = '<div class="typing-indicator" role="status" aria-label="Typing\u2026"><span class="typing-indicator-dot"></span><span class="typing-indicator-dot"></span><span class="typing-indicator-dot"></span></div>'
        nodes.push(pending)
        chat.append(...nodes)
        this._optimisticTranscribing = transcribing

        // Pin your message to the top, as a real send does (utils/chat_scroll,
        // owned by the pane's assistant-poll). A pane with no poller yet is
        // empty, so the end of it is where the message is.
        const pollSelector = "[data-controller~='assistant-poll']"
        const poller = pane.matches(pollSelector) ? pane : pane.querySelector(pollSelector)
        const poll = poller && this.application.getControllerForElementAndIdentifier(poller, "assistant-poll")
        if (poll?.pinTo) poll.pinTo(mine)
        else scroller.scrollTop = scroller.scrollHeight
    }

    // A voice send's bubble reads "Transcribing\u2026" until the words arrive.
    _setOptimisticText(text) {
        const mine = this._pane()?.querySelector(".assistant-bubble-user[data-optimistic]")
        if (mine) mine.textContent = text
        this._optimisticTranscribing = false
    }

    _removeOptimistic() {
        this._pane()?.querySelectorAll("[data-optimistic]").forEach((node) => node.remove())
        if (this._optimisticEmpty?.isConnected) this._optimisticEmpty.style.display = ""
        this._optimisticEmpty = null
        this._optimisticTranscribing = false
    }

    // Visible feedback for the length of the fetch: onClickButton's "let the
    // form submit normally" path gave no sign anything was happening, which
    // both looked broken on a slow reply and invited a second tap/Enter.
    // Mirrors universal-compose's own onSubmit, which disables its sendBtn
    // the same way while its fetch is in flight.
    _setSending(active) {
        this.element.classList.toggle("is-sending", active)
        if (this.hasButtonTarget) this.buttonTarget.disabled = active
        // A hosted pane has already cleared the field and drawn the message,
        // so say so in the field too, with the send button's spinner.
        if (this._pane() && this._state === "idle") {
            if (active) {
                this.fieldTarget.placeholder = "Sending\u2026"
                if (this.hasIconTarget) this.iconTarget.className = "bi bi-arrow-repeat assistant-spin"
                if (this.hasButtonTarget) this.buttonTarget.classList.add("is-busy")
            } else if (this.hasButtonTarget) {
                this.buttonTarget.classList.remove("is-busy")
            }
        }
    }

    onClickButton(e) {
        if (this._state === "recording") {
            e.preventDefault()
            this._recorder.stop()
            return
        }
        if (this._state === "transcribing") {
            e.preventDefault()
            return
        }
        if (this._hasText()) return // let the form submit normally
        e.preventDefault()
        this._recorder.start()
    }

    _submitVoice(text) {
        this.fieldTarget.disabled = false
        this.fieldTarget.value = text
        if (this.element.requestSubmit) this.element.requestSubmit()
        else this.element.submit()
    }

    _hasText() {
        return (this.fieldTarget?.value || "").trim().length > 0 || this._attachments.length > 0
    }

    _setState(next) {
        this._state = next
        this._sync()
        // Hosted voice send: your bubble is up the moment recording stops, and
        // _submitVoice swaps the transcript in. If the recorder lands back on
        // idle without sending (nothing caught, blocked), take it back out.
        if (next === "transcribing" && this._pane()) {
            this._showOptimistic("", [], { transcribing: true })
        } else if (next === "idle" && this._optimisticTranscribing) {
            queueMicrotask(() => {
                if (this._optimisticTranscribing && !this._submitting) this._removeOptimistic()
            })
        }
    }

    _sync() {
        const btn = this.buttonTarget
        const icon = this.iconTarget
        btn.classList.remove("is-recording", "is-busy")

        if (this._state === "recording") {
            btn.type = "button"
            btn.setAttribute("aria-label", "Stop & send")
            icon.className = "bi bi-stop-fill"
            btn.classList.add("is-recording")
            this.fieldTarget.disabled = true
            this.fieldTarget.placeholder = "Listening\u2026 tap to send"
            return
        }

        if (this._state === "transcribing") {
            btn.type = "button"
            btn.setAttribute("aria-label", "Transcribing")
            icon.className = "bi bi-arrow-repeat assistant-spin"
            btn.classList.add("is-busy")
            this.fieldTarget.disabled = true
            this.fieldTarget.placeholder = "Transcribing\u2026"
            return
        }

        // idle
        this.fieldTarget.disabled = false
        this.fieldTarget.placeholder = this._idlePlaceholder()
        if (this.hasVoiceTarget) this.voiceTarget.value = ""
        if (this._hasText()) {
            btn.type = "submit"
            btn.setAttribute("aria-label", "Send")
            icon.className = "bi bi-arrow-up"
        } else {
            btn.type = "button"
            btn.setAttribute("aria-label", "Tap to talk")
            icon.className = "bi bi-mic"
        }
    }

    _flash(msg) {
        this.fieldTarget.placeholder = msg
        setTimeout(() => {
            if (this._state === "idle") this.fieldTarget.placeholder = this._idlePlaceholder()
        }, 1800)
    }
}
